import { useCallback, useEffect, useRef, useState } from "react";
import { BadgeCheck, Copy, MoreHorizontal, PhoneCall, RefreshCw } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import { ApiError } from "../../../../lib/api";
import { formatPhone, normalizeE164 } from "../../../../lib/phone";
import {
  listCallerIds, saveTelephonySettings, startCallerIdVerification, type CallerIdRow,
} from "../../../../lib/calls";
import { Btn, Card, CardHead, EmptyState, StatusPill, listCard, useToast } from "../../../ui";
import { Pop, PopHost, useClosePops } from "../../../shell/Pop";
import { HeadTitle, IntegrationCard, PanelLoader } from "../panel/IntegrationCard";
import { Field, Note, Pill } from "../panel/fields";

/** How long and how often to watch for the number appearing as verified. */
const POLL_INTERVAL_MS = 5000;
const POLL_MAX_MS = 2 * 60 * 1000;

/**
 * The number and the name Twilio knows it by, then its state, then the menu.
 *
 * Twilio's own friendly name is the only thing that tells two of the office's
 * mobiles apart, so it gets the width rather than sharing the first cell. The
 * state cell carries two pills on the line that is in use - what it is, and that
 * it is the one calls go out on - so it has a floor of its own rather than an
 * `fr` that would break them onto two lines.
 */
const COLS = "minmax(0,1.35fr) minmax(0,1.2fr) minmax(196px,1.2fr) 44px";

/**
 * True when the number is one the account owns.
 *
 * Twilio refuses to verify its own number, so the verify form has to know: it is
 * the difference between "already usable, pick it above" and an upstream error
 * that says nothing.
 */
function isOwnNumber(rows: CallerIdRow[] | null, phoneNumber: string | null): boolean {
  if (!phoneNumber) return false;
  return (rows ?? []).some((row) => row.kind === "owned" && row.phoneNumber === phoneNumber);
}

/**
 * Settings -> Integrations: the caller ids. Twilio stays the source of truth;
 * this card lists both kinds it keeps - the numbers bought on the account and
 * the ones verified over the phone - lets the operator pick the one outbound
 * calls display, and runs the "verify my own mobile" flow (Twilio calls the
 * number, the operator keys in the code shown here).
 */
export function CallerIdCard() {
  const t = useT();
  const [rows, setRows] = useState<CallerIdRow[] | null>(null);
  const [selected, setSelected] = useState("");
  const [loadError, setLoadError] = useState(false);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");

  const [verifyNumber, setVerifyNumber] = useState("");
  const [verifyState, setVerifyState] = useState<"idle" | "starting" | "waiting" | "done" | "error" | "own">("idle");
  const [validationCode, setValidationCode] = useState("");
  const pollUntil = useRef(0);
  const pollTarget = useRef("");

  const refresh = useCallback(async () => {
    try {
      const res = await listCallerIds();
      setRows(res.callerIds);
      setSelected(res.selected);
      setLoadError(false);
      return res.callerIds;
    } catch {
      setRows((prev) => prev ?? []);
      setLoadError(true);
      return null;
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Poll while a verification is in flight; stop when the number shows up.
  useEffect(() => {
    if (verifyState !== "waiting") return;
    const timer = setInterval(async () => {
      if (Date.now() > pollUntil.current) {
        setVerifyState("error");
        return;
      }
      const ids = await refresh();
      if (ids?.some((r) => r.phoneNumber === pollTarget.current)) {
        setVerifyState("done");
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [verifyState, refresh]);

  async function choose(phoneNumber: string) {
    setSaveState("saving");
    try {
      await saveTelephonySettings({ twilio: { callerId: phoneNumber } });
      setSelected(phoneNumber);
      setSaveState("saved");
      setTimeout(() => setSaveState("idle"), 2500);
    } catch {
      setSaveState("error");
    }
  }

  async function handleVerify(e: React.FormEvent) {
    e.preventDefault();
    const normalized = normalizeE164(verifyNumber);
    if (!normalized) {
      setVerifyState("error");
      return;
    }
    // The button is already disabled for these; this is the case where the list
    // arrived after the number was typed.
    if (isOwnNumber(rows, normalized)) {
      setVerifyState("own");
      return;
    }
    setVerifyState("starting");
    setValidationCode("");
    try {
      const res = await startCallerIdVerification(normalized);
      pollTarget.current = res.phoneNumber;
      pollUntil.current = Date.now() + POLL_MAX_MS;
      setValidationCode(res.validationCode);
      setVerifyState("waiting");
    } catch (err) {
      // 409 is the server having checked the same thing against Twilio itself,
      // which knows about numbers bought since this list was loaded.
      setVerifyState(err instanceof ApiError && err.status === 409 ? "own" : "error");
    }
  }

  if (rows === null) return <PanelLoader />;

  const ownTyped = isOwnNumber(rows, normalizeE164(verifyNumber));

  return (
    // One open row menu at a time. Settings has no `PopHost` of its own, and a
    // `Pop` without one is a button that never opens anything and says nothing
    // in the console about it.
    <PopHost>
      <div className="space-y-4">
        <Card style={listCard(COLS)}>
          <CardHead
            title={<HeadTitle icon={BadgeCheck}>{t("settings.callerId.title")}</HeadTitle>}
            sub={t("settings.callerId.desc")}
            actions={
              saveState === "saved" ? (
                <StatusPill tone="ok">{t("settings.callerId.saved")}</StatusPill>
              ) : saveState === "error" ? (
                <StatusPill tone="lt">{t("settings.callerId.saveError")}</StatusPill>
              ) : null
            }
          />

          {loadError ? (
            <div className="px-[26px] pt-3">
              <Note tone="warn">{t("settings.callerId.loadError")}</Note>
            </div>
          ) : null}

          {rows.length === 0 ? (
            <EmptyState
              icon={BadgeCheck}
              title={t("settings.callerId.emptyTitle")}
              body={t("settings.callerId.empty")}
            />
          ) : (
            <>
              <div className="crm-list-head" style={{ gridTemplateColumns: COLS, paddingTop: 18 }}>
                <span>{t("settings.callerId.col.number")}</span>
                <span>{t("settings.callerId.col.name")}</span>
                <span>{t("settings.callerId.col.status")}</span>
                <span />
              </div>

              <div className="crm-rows pt-0">
                {rows.map((row) => (
                  // Keyed by the number rather than the sid: the number is what
                  // the two Twilio lists were de-duplicated on, so it is the one
                  // value guaranteed unique across the merged rows.
                  <CallerIdRowView
                    key={row.phoneNumber}
                    row={row}
                    active={row.phoneNumber === selected}
                    busy={saveState === "saving"}
                    onChoose={() => void choose(row.phoneNumber)}
                  />
                ))}
              </div>
            </>
          )}
        </Card>

        <IntegrationCard
          icon={PhoneCall}
          title={t("settings.callerId.verifyTitle")}
          desc={t("settings.callerId.verifyDesc")}
        >
          <form onSubmit={handleVerify} className="space-y-4">
            <div className="flex flex-wrap items-end gap-2.5">
              {/* The button acts on the account, not on the box, so it stays
                  outside the field's label. */}
              <Field label={t("settings.callerId.number")} className="flex-1 sm:max-w-[260px]">
                <Pill
                  value={verifyNumber}
                  onChange={(value) => {
                    setVerifyNumber(value);
                    // A refusal must not outlive the number it was about. Only
                    // the settled answers are cleared - a verification still in
                    // flight keeps its code on screen.
                    if (verifyState === "own" || verifyState === "error") setVerifyState("idle");
                  }}
                  placeholder="+372 5xxx xxxx"
                  autoComplete="off"
                  inputMode="tel"
                  mono
                />
              </Field>
              <Btn
                type="submit"
                tone="dark"
                disabled={ownTyped || verifyState === "starting" || verifyState === "waiting"}
              >
                {verifyState === "starting" ? (
                  <RefreshCw className="size-3.5 animate-spin" />
                ) : (
                  <PhoneCall className="size-3.5" />
                )}
                {verifyState === "starting" ? t("settings.callerId.starting") : t("settings.callerId.verify")}
              </Btn>
            </div>

            {/* Said while the number is being typed, not after a failed
                attempt: Twilio refuses to verify a number the account owns, and
                there is nothing to fix - it is already usable. */}
            {ownTyped || verifyState === "own" ? (
              <Note tone="info" live>{t("settings.callerId.ownNumber")}</Note>
            ) : null}

            {verifyState === "waiting" && validationCode ? (
              <div className="rounded-[24px] bg-[var(--crm-sunk)] px-5 py-6 text-center">
                <p className="crm-sub !mt-0 text-[12.5px]">{t("settings.callerId.codeHint")}</p>
                <p className="mt-2.5 font-mono text-[32px] font-bold tracking-[0.3em]">{validationCode}</p>
                <p className="crm-sub !mt-2.5 inline-flex items-center justify-center gap-1.5 text-[11.5px]">
                  <RefreshCw className="size-3 animate-spin" />
                  {t("settings.callerId.waiting")}
                </p>
              </div>
            ) : null}

            {verifyState === "done" ? (
              <Note tone="ok" live>{t("settings.callerId.verified")}</Note>
            ) : null}
            {verifyState === "error" ? (
              <Note tone="bad" live>{t("settings.callerId.verifyError")}</Note>
            ) : null}
          </form>
        </IntegrationCard>
      </div>
    </PopHost>
  );
}

function CallerIdRowView({
  row,
  active,
  busy,
  onChoose,
}: {
  row: CallerIdRow;
  active: boolean;
  busy: boolean;
  onChoose: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const close = useClosePops();

  async function copyNumber() {
    close();
    try {
      await navigator.clipboard.writeText(row.phoneNumber);
      toast(t("common.copied"));
    } catch {
      toast(t("common.copyFailed"));
    }
  }

  return (
    <div className="crm-list-row" style={{ gridTemplateColumns: COLS }}>
      <span className="crm-prow-n crm-trunc font-mono">{formatPhone(row.phoneNumber)}</span>
      <span className="crm-mut crm-trunc text-[12.5px]">{row.friendlyName || "-"}</span>
      {/* What the number IS stays on the row even when it is the one in use:
          "owned" is why no verification can expire under it, and that is worth
          reading at a glance rather than only on the rows nothing is set on. */}
      <span className="flex min-w-0 flex-wrap items-center gap-1.5">
        {active ? <StatusPill tone="ok">{t("settings.callerId.inUse")}</StatusPill> : null}
        <StatusPill tone={row.kind === "owned" ? "info" : "mut"}>
          {t(row.kind === "owned" ? "settings.callerId.statusOwned" : "settings.callerId.statusVerified")}
        </StatusPill>
      </span>

      <span className="justify-self-end">
        <Pop
          trigger={(open) => (
            <button
              type="button"
              className={`crm-cb${open ? " is-on" : ""}`}
              title={t("common.more")}
              aria-label={t("common.more")}
              aria-haspopup="menu"
              aria-expanded={open}
            >
              <MoreHorizontal className="size-4" />
            </button>
          )}
        >
          {!active ? (
            <button
              type="button"
              className="crm-mi"
              disabled={busy}
              onClick={() => {
                close();
                onChoose();
              }}
            >
              <PhoneCall className="size-4" />
              {t("settings.callerId.use")}
            </button>
          ) : null}
          <button type="button" className="crm-mi" onClick={() => void copyNumber()}>
            <Copy className="size-4" />
            {t("settings.callerId.copyNumber")}
          </button>
        </Pop>
      </span>
    </div>
  );
}
